import { Api } from 'src/common';
import { authenticate } from './auth-service';
import { Environment } from 'src/Environment';

export type TerminalDetails = {
  primaryContactNumber: string;
  secondaryContactNumber: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
};

export type UpdateTerminalDetails = {
  requestId: string;
  requestedDateTime: string;
  primaryContactNumber: string;
  secondaryContactNumber: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
};

type TerminalDetailsResponse = {
  ok: boolean;
  message?: string;
  responseCode?: number;
  errorMessage?: string;
  response?: TerminalDetails;
};

type TerminalResponse = {
  ok: boolean;
  message?: string;
  responseCode?: number;
  errorMessage?: string;
  data?: TerminalDetails;
};

export async function getTerminalDetails(
  abortController: AbortController,
  merchantId: string,
  terminalId: string,
): Promise<TerminalResponse> {
  const accessToken = await authenticate();
  if (!terminalId || !accessToken) {
    throw new Error('Unable to get terminal details');
  }

  const endpoint = `/merchants/${merchantId}/terminals/${terminalId}`;

  const { response, status } = await Api.get<TerminalDetails>(
    Environment.current.privateBaseURL + endpoint,
    {
      signal: abortController.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (response && status === 200) {
    return {
      ok: true,
      data: response,
      responseCode: status,
      message: '',
    };
  }

  if (status > 300 && status < 500) {
    return {
      ok: false,
      data: response,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      responseCode: status,
    };
  }

  throw new Error(
    `Get Merchant Terminal details: Invalid response: ${status}: ${JSON.stringify(response)}`,
  );
}

export async function updateTerminalDetails(
  abortController: AbortController,
  data: UpdateTerminalDetails,
  merchantId: string,
  terminalId: string,
): Promise<TerminalDetailsResponse> {
  const accessToken = await authenticate();
  const endpoint = `/merchants/${merchantId}/${terminalId}/terminalUpdateRequest`;

  const result = await Api.post<TerminalDetailsResponse>(
    Environment.current.privateBaseURL + endpoint,
    {
      signal: abortController.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  );

  if (result && result?.response && (result.status === 200 || result.status === 202)) {
    return {
      ok: true,
      message: result.response.message,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      message:
        result.response?.message ??
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Update Terminal details: Invalid response: ${result.status}: ${JSON.stringify(
      result.response,
    )}`,
  );
}
